import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';

import SearchBar from '../../components/searchbar';

import LSRestedService, { reportsFields } from '../../data/lsrested-service';

import Helpers from '../../libraries/helpers';
import useWindowSize from '../../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
}));

const Reports = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { height } = useWindowSize();
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const reportTableAPIRef = useApiRef();
    const [reportSortModel, setReportSortModel] = useState([{ field: 'data.title', sort: 'asc' }]);
    const [search, setSearch] = useState("");
    const [searchData, setSearchData] = useState(null);

    // Search state
    const [reportSearch, setReportSearch] = useState('');

    useEffect(() => {
        getReports();
    }, []);

    const getReports = () => {
        LSRestedService.getLicenses().then(res => {
            if (res?.objects) {
                setReports(res.objects);
            }
        });
    };

    /* eslint-disable react/display-name */
    const tableRenderers = {
        license: d => {<div>{d.row.license}</div>},
        validUntil: d => {<div>{d.row.vu}</div>},
        sn: d => {<div>{d.row.sn}</div>},
        mac: d => {<div>{d.row.mac}</div>},
        created: d => {
            const date = Moment(d.row.created);
            return date.isValid() ? date.format(theme.date.long) : '';
        },
        organization: d => {<div>{d.row.organization}</div>},
    };

    const reportsColumns = reportsFields.map(f => {
        return {
            ...f,
            ...({ flex: 1 }),
            valueGetter: d => _get(d, `row.${f.field}`, ''),
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleSelectReport = (s) => {
        setSelectedReport(s.row);
    };

    const handleReportSortModelChange = (sortModel) => {
        setReportSortModel(sortModel);
    };

    const handleReportSearch = ({ search }) => {
        setSearch(search);
    };

    useEffect(() => {
        runSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const runSearch = (search) => {
        const SEARCH_FIELDS = ['created', 'license', 'valid_until', 'sn', 'mac', 'created_by', 'organization'];
        if (!search) {
            setSearchData(null);
            return;
        }

        setSearchData(reports.filter(v => {
            for (let x in v) {
                if (SEARCH_FIELDS.includes(x) && Object.prototype.hasOwnProperty.call(v, x)) {
                    if (String(v[x]).includes(search)) {
                        return v;
                    }
                }
            }
            return null;
        }).filter(x => x));
    };

    const jsonToCSV = (data) => {
        let csv = 'created,license,valid_from,valid_until,sn,mac,cores,total_cores,gbps,etpro,created_by,email,note,organization\n';
        if (data && data.length) {
            data.forEach((v) => {
                csv += (v.created || '').replace(',', '') + ',' +
                (v.license || '') + ',' +
                (v.valid_from || '').replace(',', '') + ',' +
                (v.valid_until || '').replace(',', '') + ',' +
                (v.sn || '') + ',' +
                (v.mac || '') + ',' +
                (v.cores || '') + ',' +
                (v.total_cores || '') + ',' +
                (v.gbps || '') + ',' +
                (v.etpro || '') + ',' +
                (v.created_by || '') + ',' +
                (v.email || '') + ',' +
                (v.note || '').replace(',', '') + ',' +
                (v.organization || '').replace(',', '') + ',' +
                '\n';
            });
        }

        return csv;
    };

    const handleDownloadCSV = () => {
        let csv = jsonToCSV(reports);
        let blob = new Blob([csv], { type: 'text/csv' });
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = 'licenses.csv';
        document.body.appendChild(a);
        a.click();
    };

    return (
        <Box mt={3}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <SearchBar
                        immediate
                        value={search}
                        onSearch={handleReportSearch}
                        searchservice={{ searchPlaceholder: 'Search licenses' }}
                    />
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Button variant="contained" onClick={handleDownloadCSV}><AddIcon /> Download</Button>
                </Grid>
                <Grid item xs={12}>
                    <Box className={classes.fullWidth} height={height - 300} style={{ minHeight: height - 300 }}>
                        <DataGridPro
                            apiRef={reportTableAPIRef}
                            getRowId={r => (r.license)}
                            rows={searchData || reports}
                            columns={reportsColumns}
                            disableMultipleSelection
                            disableMultipleColumnsFiltering
                            disableMultipleColumnsSorting
                            disableColumnMenu
                            disableVirtualization
                            onRowClick={handleSelectReport}
                            sortingOrder={['desc', 'asc']}
                            sortModel={reportSortModel}
                            onSortModelChange={handleReportSortModelChange}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Reports;
